import {action, computed, makeObservable, observable, runInAction} from "mobx";
import {IVillageCalendarService} from "../../../services/villageCalendarService";
import {
  VillageCalendarConfig,
  VillageCalendarConfigPayload
} from "../../../shared/interfaces/villageCalendarConfig";

/**
 * A group of village calendars belonging to a single village.
 */
export interface VillageCalendarGroup {
  villageId: string;
  villageName: string;
  villageCode: string;
  calendars: VillageCalendarConfig[];
}

export interface IVillageCalendarsDomainStore {
  villageCalendars: VillageCalendarConfig[];
  villageCalendarsByVillage: VillageCalendarGroup[];
  /**
   * The set of village ids that have at least one calendar shared across
   * more than one person type (`personTypeIds.length > 1`). This is the
   * CMS-enriched source of truth for `hasSharedCalendar`, since the plain
   * Integration API villages payload used by `AuthUser` never carries
   * `personTypeIds`.
   */
  villageIdsWithSharedCalendar: ReadonlySet<string>;
  /**
   * True only after an unscoped `getVillageCalendars()` call has loaded every
   * village's calendars. Scoped (per-village) loads leave this false, so
   * consumers needing CMS mappings for arbitrary villages must not reuse a
   * partially populated cache.
   */
  hasAllVillageCalendarsLoaded: boolean;
  getVillageCalendars: (
    abortController?: AbortController,
    villageId?: string
  ) => Promise<void>;
  getVillageCalendar: (id: string) => VillageCalendarConfig | undefined;
  createVillageCalendar: (
    payload: VillageCalendarConfigPayload
  ) => Promise<VillageCalendarConfig>;
  updateVillageCalendar: (
    id: string,
    payload: VillageCalendarConfigPayload
  ) => Promise<VillageCalendarConfig>;
  deleteVillageCalendar: (id: string) => Promise<void>;
}

export class VillageCalendarsDomainStore
  implements IVillageCalendarsDomainStore
{
  private readonly service: IVillageCalendarService;
  private loadedVillageCalendars: "all" | Set<string> = new Set<string>();
  villageCalendars: VillageCalendarConfig[] = [];

  constructor(service: IVillageCalendarService) {
    makeObservable(this, {
      villageCalendars: observable,
      villageCalendarsByVillage: computed,
      villageIdsWithSharedCalendar: computed,
      getVillageCalendars: action,
      createVillageCalendar: action,
      updateVillageCalendar: action,
      deleteVillageCalendar: action
    });
    this.service = service;
  }

  get villageCalendarsByVillage(): VillageCalendarGroup[] {
    const groups = new Map<string, VillageCalendarGroup>();
    for (const calendar of this.villageCalendars) {
      const key = String(calendar.villageId);
      const existing = groups.get(key);
      if (existing) {
        existing.calendars.push(calendar);
      } else {
        groups.set(key, {
          villageId: key,
          villageName: calendar.villageName ?? `Village ${calendar.villageId}`,
          villageCode: calendar.villageCode ?? "",
          calendars: [calendar]
        });
      }
    }
    return Array.from(groups.values()).sort((a, b) =>
      a.villageName.localeCompare(b.villageName)
    );
  }

  get villageIdsWithSharedCalendar(): ReadonlySet<string> {
    return new Set(
      this.villageCalendars
        .filter(
          (calendar) =>
            (calendar.personTypeIds?.filter((id) => !!id).length ?? 0) > 1
        )
        .map((calendar) => String(calendar.villageId))
    );
  }

  get hasAllVillageCalendarsLoaded(): boolean {
    return this.loadedVillageCalendars === "all";
  }

  private hasVillageCalendarsLoaded = (villageId: string) =>
    this.loadedVillageCalendars === "all" ||
    this.loadedVillageCalendars.has(String(villageId));

  getVillageCalendars = async (
    abortController?: AbortController,
    villageId?: string
  ) => {
    if (villageId && this.hasVillageCalendarsLoaded(villageId)) {
      return;
    }

    const data = await this.service.getVillageCalendars(
      abortController,
      villageId
    );
    const result = Array.isArray(data) ? data : [];

    runInAction(() => {
      if (villageId) {
        const scopedVillageId = String(villageId);
        this.villageCalendars = [
          ...this.villageCalendars.filter(
            (calendar) => String(calendar.villageId) !== scopedVillageId
          ),
          ...result
        ];

        if (this.loadedVillageCalendars !== "all") {
          this.loadedVillageCalendars.add(scopedVillageId);
        }
      } else {
        this.villageCalendars = result;
        this.loadedVillageCalendars = "all";
      }
    });
  };

  getVillageCalendar = (id: string) =>
    this.villageCalendars.find((c) => c.id === id);

  createVillageCalendar = async (payload: VillageCalendarConfigPayload) => {
    const created = await this.service.createVillageCalendar(payload);
    runInAction(() => {
      this.villageCalendars = [...this.villageCalendars, created];
    });
    return created;
  };

  updateVillageCalendar = async (
    id: string,
    payload: VillageCalendarConfigPayload
  ) => {
    const updated = await this.service.updateVillageCalendar(id, payload);
    runInAction(() => {
      this.villageCalendars = this.villageCalendars.map((c) =>
        c.id === id ? {...c, ...updated} : c
      );
    });
    return updated;
  };

  deleteVillageCalendar = async (id: string) => {
    await this.service.deleteVillageCalendar(id);
    runInAction(() => {
      this.villageCalendars = this.villageCalendars.filter((c) => c.id !== id);
    });
  };
}
